import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import {
  Camera,
  X,
  Trash2,
  Loader2,
  FlipHorizontal,
  Eye,
  Zap,
  ZapOff,
} from "lucide-react";
import { toast } from "sonner";

interface CapturedImage {
  id: number;
  src: string;
  isBlurry: boolean;
  score: number;
  width: number;
  height: number;
}

const CameraPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get passed state from Evaluate page
  const { examType, subject } = (location.state as { examType?: string; subject?: string }) || {};

  // Camera state
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [images, setImages] = useState<CapturedImage[]>([]);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [previewImage, setPreviewImage] = useState<CapturedImage | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [isSwitchingCamera, setIsSwitchingCamera] = useState(false);

  // Flash/Torch state
  const [isFlashOn, setIsFlashOn] = useState(false);
  const [isFlashSupported, setIsFlashSupported] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Start camera
  const startCamera = useCallback(async () => {
    try {
      setCameraError(null);
      setIsCameraReady(false);

      // Stop existing stream
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        },
        audio: false
      });

      setStream(mediaStream);

      // Check if torch is supported
      const videoTrack = mediaStream.getVideoTracks()[0];
      const capabilities = videoTrack.getCapabilities?.() as any;
      setIsFlashSupported(capabilities?.torch === true);

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.onloadedmetadata = () => {
          setIsCameraReady(true);
        };
      }
    } catch (error: any) {
      console.error("Camera access error:", error);
      if (error.name === 'NotAllowedError') {
        setCameraError("Camera access denied. Please allow camera permissions.");
      } else if (error.name === 'NotFoundError') {
        setCameraError("No camera found on this device.");
      } else {
        setCameraError("Failed to access camera. Please try again.");
      }
    }
  }, [facingMode, stream]);

  // Toggle flash/torch
  const toggleFlash = async () => {
    if (!stream || !isFlashSupported) return;

    try {
      const videoTrack = stream.getVideoTracks()[0];
      const newFlashState = !isFlashOn;
      await videoTrack.applyConstraints({
        advanced: [{ torch: newFlashState } as any]
      });
      setIsFlashOn(newFlashState);
    } catch (error) {
      console.error("Flash toggle error:", error);
      toast.error("Failed to toggle flash");
    }
  };

  // Initialize camera on mount
  useEffect(() => {
    startCamera();

    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Restart camera when facing mode changes
  useEffect(() => {
    const switchCameraStream = async () => {
      setIsSwitchingCamera(true);
      setIsFlashOn(false); // Reset flash when switching
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      await startCamera();
      setIsSwitchingCamera(false);
    };
    switchCameraStream();
  }, [facingMode]);

  // --- Algorithm: Blur Detection (Variance of Laplacian) ---
  // Optimized for mobile devices - analyzes center region for better accuracy
  const detectBlur = (imageSrc: string): Promise<{ isBlurry: boolean; score: number }> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = imageSrc;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        // Use higher resolution for better detection
        const width = 1000;
        const scaleFactor = width / img.width;
        const height = Math.round(img.height * scaleFactor);

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) {
          resolve({ isBlurry: false, score: 0 });
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);

        // Analyze center 60% of image (where document content is)
        const marginX = Math.floor(width * 0.2);
        const marginY = Math.floor(height * 0.2);
        const analyzeWidth = width - (marginX * 2);
        const analyzeHeight = height - (marginY * 2);

        const imageData = ctx.getImageData(marginX, marginY, analyzeWidth, analyzeHeight);
        const data = imageData.data;

        // Convert to grayscale
        const grayData = new Uint8ClampedArray(analyzeWidth * analyzeHeight);
        for (let i = 0; i < data.length; i += 4) {
          grayData[i / 4] = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
        }

        // Calculate Laplacian variance
        let mean = 0;
        const laplacianValues: number[] = [];

        for (let y = 1; y < analyzeHeight - 1; y++) {
          for (let x = 1; x < analyzeWidth - 1; x++) {
            const idx = y * analyzeWidth + x;
            // Full 8-neighbor Laplacian kernel for better edge detection
            const laplacian =
              grayData[idx - analyzeWidth - 1] + grayData[idx - analyzeWidth] + grayData[idx - analyzeWidth + 1] +
              grayData[idx - 1] + grayData[idx + 1] +
              grayData[idx + analyzeWidth - 1] + grayData[idx + analyzeWidth] + grayData[idx + analyzeWidth + 1]
              - (8 * grayData[idx]);
            laplacianValues.push(Math.abs(laplacian));
            mean += Math.abs(laplacian);
          }
        }

        mean /= laplacianValues.length;

        let variance = 0;
        for (let i = 0; i < laplacianValues.length; i++) {
          variance += Math.pow(laplacianValues[i] - mean, 2);
        }
        variance /= laplacianValues.length;

        // Higher threshold for stricter blur detection on mobile
        // Mobile cameras typically score 800+ for clear images
        const threshold = 500;
        console.log(`Blur Score: ${variance.toFixed(2)} vs Threshold: ${threshold}`);

        resolve({ isBlurry: variance < threshold, score: Math.round(variance) });
      };
      img.onerror = () => {
        resolve({ isBlurry: false, score: 0 });
      };
    });
  };

  // Capture image
  const captureImage = async () => {
    if (!videoRef.current || !canvasRef.current || isCapturing) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    setIsCapturing(true);

    try {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageSrc = canvas.toDataURL('image/jpeg', 0.85);

      // Flash effect
      const flash = document.createElement('div');
      flash.className = 'fixed inset-0 bg-white z-[60] pointer-events-none';
      flash.style.animation = 'flash 150ms ease-out';
      document.body.appendChild(flash);
      setTimeout(() => flash.remove(), 150);

      // Check blur
      const { isBlurry, score } = await detectBlur(imageSrc);

      if (isBlurry) {
        // Show red warning toast at top-center
        toast.error("Blurry Image! Hold steady and try again", { position: 'top-center' });
        setIsCapturing(false);
        return;
      }

      // Save clear image
      const newImage: CapturedImage = {
        id: Date.now(),
        src: imageSrc,
        isBlurry: false,
        score,
        width: canvas.width,
        height: canvas.height
      };

      setImages(prev => [...prev, newImage]);
      toast.success(`Page ${images.length + 1} captured!`, { position: 'top-center' });
    } catch (error) {
      console.error("Capture error:", error);
      toast.error("Failed to capture image. Please try again.");
    } finally {
      setIsCapturing(false);
    }
  };

  // Delete image
  const deleteImage = (id: number) => {
    setImages(prev => prev.filter(img => img.id !== id));
    if (previewImage?.id === id) {
      setPreviewImage(null);
    }
  };

  // Retake (clear all and restart)
  const retakeAll = () => {
    setImages([]);
    toast.info("All images cleared. Start scanning again.");
  };

  // Switch camera
  const switchCamera = () => {
    setFacingMode(prev => prev === 'environment' ? 'user' : 'environment');
  };

  // Convert base64 to File
  const dataURLtoFile = (dataURL: string, filename: string): File => {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  };

  // Navigate to evaluate page with images for preview
  const handlePreviewImages = () => {
    if (images.length === 0) {
      toast.error("Please capture at least one page.");
      return;
    }

    // Convert captured images to File objects
    const imageFiles = images.map((img, idx) =>
      dataURLtoFile(img.src, `scanned_page_${idx + 1}.jpg`)
    );

    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }

    navigate('/evaluate', {
      state: {
        capturedImages: imageFiles,
        openReviewModal: true,
        examType,
        subject
      }
    });

    toast.success(`${images.length} page(s) ready for preview!`);
  };

  // Go back to evaluate page
  const handleClose = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    navigate('/evaluate', {
      state: { examType, subject }
    });
  };

  return (
    <div className="fixed inset-0 bg-black flex flex-col">
      {/* Add flash animation style */}
      <style>{`
        @keyframes flash {
          0% { opacity: 1; }
          100% { opacity: 0; }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }
      `}</style>

      {/* Header - Safe area padding */}
      <div className="relative z-20 pt-2 px-4 pb-2 flex items-center justify-between bg-black/90 safe-area-top">
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
        >
          <X className="w-5 h-5 text-white" />
        </button>

        <div className="text-center flex-1">
          <h1 className="text-white font-bold text-base">Scan Pages</h1>
          <p className="text-white/60 text-xs">{images.length} page(s) captured</p>
        </div>

        {/* Flash Toggle */}
        <button
          onClick={toggleFlash}
          disabled={!isFlashSupported || facingMode === 'user'}
          className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
            !isFlashSupported || facingMode === 'user'
              ? 'bg-white/5 opacity-30'
              : isFlashOn
                ? 'bg-yellow-500 text-black'
                : 'bg-white/10 hover:bg-white/20'
          }`}
        >
          {isFlashOn ? (
            <Zap className="w-5 h-5" />
          ) : (
            <ZapOff className="w-5 h-5 text-white" />
          )}
        </button>
      </div>

      {/* Camera View - Takes remaining space */}
      <div className="flex-1 relative overflow-hidden">
        {cameraError ? (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-900 p-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Camera className="w-10 h-10 text-red-400" />
              </div>
              <h2 className="text-white text-xl font-bold mb-2">Camera Error</h2>
              <p className="text-gray-400 mb-6">{cameraError}</p>
              <Button
                onClick={startCamera}
                className="bg-white text-black hover:bg-gray-200"
              >
                Try Again
              </Button>
            </div>
          </div>
        ) : (
          <>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="absolute inset-0 w-full h-full object-cover"
            />

            {/* Loading overlay */}
            {(!isCameraReady || isSwitchingCamera) && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-10">
                <div className="text-center">
                  <Loader2 className="w-10 h-10 text-white animate-spin mx-auto mb-3" />
                  <p className="text-white/80">
                    {isSwitchingCamera ? 'Switching camera...' : 'Starting camera...'}
                  </p>
                </div>
              </div>
            )}

            {/* Document frame guide */}
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute inset-4 border-2 border-white/30 rounded-lg">
                <div className="absolute -top-1 -left-1 w-8 h-8 border-t-4 border-l-4 border-white rounded-tl-lg" />
                <div className="absolute -top-1 -right-1 w-8 h-8 border-t-4 border-r-4 border-white rounded-tr-lg" />
                <div className="absolute -bottom-1 -left-1 w-8 h-8 border-b-4 border-l-4 border-white rounded-bl-lg" />
                <div className="absolute -bottom-1 -right-1 w-8 h-8 border-b-4 border-r-4 border-white rounded-br-lg" />
              </div>
            </div>

            {/* Capture indicator */}
            {isCapturing && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/10 backdrop-blur-sm z-10">
                <Loader2 className="w-12 h-12 text-white animate-spin" />
              </div>
            )}
          </>
        )}

        {/* Hidden canvas for capture */}
        <canvas ref={canvasRef} className="hidden" />

        {/* Captured Images Thumbnails - At bottom of camera view */}
        {images.length > 0 && (
          <div className="absolute bottom-4 left-0 right-0 px-4 z-10">
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
              {images.map((img, idx) => (
                <motion.div
                  key={img.id}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="relative flex-shrink-0"
                >
                  <div
                    onClick={() => setPreviewImage(img)}
                    className="w-14 h-18 rounded-lg overflow-hidden border-2 border-white/50 cursor-pointer hover:border-white transition-colors"
                  >
                    <img
                      src={img.src}
                      alt={`Page ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] text-center py-0.5">
                      {idx + 1}
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteImage(img.id);
                    }}
                    className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center shadow-lg"
                  >
                    <X className="w-3 h-3 text-white" />
                  </button>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Bottom Controls - Fixed height */}
      <div className="relative z-20 bg-black/95 px-4 pt-4 pb-6 safe-area-bottom">
        {/* Camera Switch Button */}
        <div className="flex justify-center mb-4">
          <button
            onClick={switchCamera}
            disabled={isSwitchingCamera}
            className={`flex items-center gap-2 px-4 py-2 rounded-full transition-colors ${
              isSwitchingCamera
                ? 'bg-white/5 opacity-50'
                : 'bg-white/10 hover:bg-white/20'
            }`}
          >
            {isSwitchingCamera ? (
              <Loader2 className="w-4 h-4 text-white animate-spin" />
            ) : (
              <FlipHorizontal className="w-4 h-4 text-white" />
            )}
            <span className="text-white text-sm font-medium">
              {facingMode === 'environment' ? 'Back Camera' : 'Front Camera'}
            </span>
          </button>
        </div>

        {/* Main Controls */}
        <div className="flex items-center justify-center gap-8 max-w-xs mx-auto">
          {/* Retake Button */}
          <button
            onClick={retakeAll}
            disabled={images.length === 0}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
              images.length === 0
                ? 'bg-white/10 text-white/30'
                : 'bg-white/20 text-white hover:bg-white/30'
            }`}
          >
            <Trash2 className="w-5 h-5" />
          </button>

          {/* Capture Button */}
          <button
            onClick={captureImage}
            disabled={!isCameraReady || isCapturing || !!cameraError}
            className={`w-18 h-18 rounded-full border-4 border-white flex items-center justify-center transition-all ${
              !isCameraReady || isCapturing || cameraError
                ? 'bg-white/10 opacity-50'
                : 'bg-white/20 hover:bg-white/30 active:scale-95'
            }`}
            style={{ width: '72px', height: '72px' }}
          >
            <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center">
              {isCapturing ? (
                <Loader2 className="w-6 h-6 text-gray-800 animate-spin" />
              ) : (
                <Camera className="w-6 h-6 text-gray-800" />
              )}
            </div>
          </button>

          {/* Placeholder for symmetry */}
          <div className="w-12 h-12" />
        </div>

        {/* Preview Images Button */}
        {images.length > 0 && (
          <div className="mt-4 max-w-sm mx-auto">
            <Button
              onClick={handlePreviewImages}
              className="w-full bg-indigo-500 hover:bg-indigo-600 text-white h-11"
            >
              <Eye className="w-4 h-4 mr-2" />
              Preview Images
            </Button>
          </div>
        )}
      </div>

      {/* Image Preview Modal */}
      <AnimatePresence>
        {previewImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
            onClick={() => setPreviewImage(null)}
          >
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute top-4 right-4 w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20"
            >
              <X className="w-6 h-6 text-white" />
            </button>

            <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
              <span className="text-white font-medium">
                Page {images.findIndex(img => img.id === previewImage.id) + 1} of {images.length}
              </span>
            </div>

            <motion.img
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              src={previewImage.src}
              alt="Preview"
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />

            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-3">
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  deleteImage(previewImage.id);
                }}
                variant="outline"
                className="bg-red-500/20 border-red-500/50 text-red-400 hover:bg-red-500/30"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default CameraPage;
